import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Flex, Tooltip, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';

const { Text } = Typography;

type Props = {
  pageNumber: number;
  width: number;
  height: number;
  // De eerste positie zonder side: hier kan de gebruiker direct een bestand uploaden (klik of
  // drop) of een lege pagina invoegen. Alle andere placeholders zijn passief.
  isFirstFree: boolean;
  disabled: boolean;
  canUpload: boolean;
  onUploadFiles: (files: File[]) => void;
  onInsertBlankPage: () => void;
};

// Positie waar nog geen side voor bestaat: gestippeld vakje met alleen het paginanummer.
const PagePlaceholder = ({
  pageNumber,
  width,
  height,
  isFirstFree,
  disabled,
  canUpload,
  onUploadFiles,
  onInsertBlankPage,
}: Props) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);

  const isActive = isFirstFree && !disabled;
  const showDropTarget = isActive && canUpload;

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    if (!showDropTarget) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) onUploadFiles(files);
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width }}
      title={isFirstFree ? undefined : (t('BookLayout.PlaceholderHint') as string)}
    >
      <Text
        style={{ fontSize: '0.8rem', fontWeight: 500, color: isFirstFree ? '#616161' : '#bfbfbf' }}
      >
        {pageNumber}
      </Text>
      <div
        onDragOver={(event) => {
          if (!showDropTarget) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = 'copy';
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        style={{
          width,
          height,
          boxSizing: 'border-box',
          border: isDragOver
            ? '2px dashed #35b3ee'
            : isFirstFree
              ? '1px dashed #8c8c8c'
              : '1px dashed #d9d9d9',
          borderRadius: 2,
          backgroundColor: isDragOver ? '#e6f7ff' : isFirstFree ? '#f5f5f5' : '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: 6,
          transition: 'border-color 0.15s, background-color 0.15s',
        }}
      >
        {isFirstFree && (
          <Flex gap={8} align="center" justify="center">
            {canUpload && (
              <Tooltip
                title={
                  <>
                    <div style={{ fontWeight: 600 }}>{t('BookLayout.UploadFile')}</div>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>{t('BookLayout.DropHint')}</div>
                  </>
                }
              >
                <Upload
                  multiple
                  showUploadList={false}
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={disabled}
                  // Zelf uploaden via uploadService; antd mag geen eigen request doen en niets
                  // in zijn lijst bewaren. beforeUpload krijgt per bestand de hele geselecteerde
                  // batch mee — één keer (bij het eerste bestand) alles doorgeven.
                  beforeUpload={(file: RcFile, batch: RcFile[]) => {
                    if (file === batch[0]) onUploadFiles(batch);
                    return Upload.LIST_IGNORE;
                  }}
                >
                  <Button
                    shape="circle"
                    type="primary"
                    icon={<UploadOutlined />}
                    disabled={disabled}
                    aria-label={t('BookLayout.UploadFile') as string}
                    style={{ backgroundColor: '#35b3ee', borderColor: '#35b3ee' }}
                  />
                </Upload>
              </Tooltip>
            )}
            <Tooltip title={t('BookPreview.InsertBlankPageHere')}>
              <Button
                shape="circle"
                icon={<PlusOutlined />}
                disabled={disabled}
                onClick={onInsertBlankPage}
                aria-label={t('BookPreview.InsertBlankPageHere') as string}
              />
            </Tooltip>
          </Flex>
        )}
      </div>
    </div>
  );
};

export default PagePlaceholder;
